// Phase 1G.1 — Shared read-only deep-link highlight hook.
// Reads either a URL hash fragment (e.g. "#rule=rule_123") or a query param
// (e.g. "?employee=emp_321") and, when the matching DOM node carrying the
// expected data-testid is mounted, scrolls it into view and flashes a 2-second
// amber ring. Strictly cosmetic — no writes, no state changes anywhere.
//
// Usage:
//   useHashHighlight({ key: "rule", testidPrefix: "rule-row-", deps: [rules] });
//   useQueryHighlight({ key: "employee", testidPrefix: "employee-row-", deps: [employees] });

import { useEffect } from "react";

const HIGHLIGHT_CLASSES = [
  "ring-2",
  "ring-amber-300",
  "ring-offset-2",
  "transition-shadow",
];

function flash(node) {
  if (!node) return;
  node.scrollIntoView({ behavior: "smooth", block: "center" });
  node.classList.add(...HIGHLIGHT_CLASSES);
  window.setTimeout(() => {
    node.classList.remove(...HIGHLIGHT_CLASSES);
  }, 2000);
}

function parseHash() {
  const raw = (window.location.hash || "").replace(/^#/, "");
  const out = {};
  raw.split("&").filter(Boolean).forEach((kv) => {
    const [k, v] = kv.split("=");
    if (k) out[decodeURIComponent(k)] = decodeURIComponent(v || "");
  });
  return out;
}

export function useHashHighlight({ key, testidPrefix, deps = [], onMatch }) {
  useEffect(() => {
    const params = parseHash();
    const id = params[key];
    if (!id) return;
    if (onMatch) onMatch(id, params);
    let attempts = 0;
    const tick = () => {
      const node = document.querySelector(
        `[data-testid="${testidPrefix}${id}"]`,
      );
      if (node) {
        flash(node);
        return;
      }
      if (attempts++ < 20) window.setTimeout(tick, 100);
    };
    tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export function useQueryHighlight({ key, testidPrefix, deps = [], onMatch }) {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get(key);
    if (!id) return;
    if (onMatch) onMatch(id);
    let attempts = 0;
    const tick = () => {
      const node = document.querySelector(
        `[data-testid="${testidPrefix}${id}"]`,
      );
      if (node) {
        flash(node);
        return;
      }
      if (attempts++ < 20) window.setTimeout(tick, 100);
    };
    tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
